import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mic, MicOff, PhoneCall, PhoneOff, Volume2, Loader2, TicketCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * VoicebotDemo — demo in-browser di un risponditore AI a chiamate.
 * Usa Web Speech API (STT + TTS) del browser: nessun servizio esterno di telefonia.
 * La conversazione viene mandata al backend che risponde via Gemini e può eseguire
 * azioni reali (lookup dispenser, apertura ticket) collegate al DB.
 *
 * Nota: SpeechRecognition è disponibile in Chrome/Edge/Safari desktop e Chrome Android.
 * Firefox non lo supporta — mostriamo un avviso.
 */
export default function VoicebotDemo() {
  const [supported, setSupported] = useState(true);
  const [callActive, setCallActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant'|'system', text, ticket?}
  const [interim, setInterim] = useState("");
  const [muted, setMuted] = useState(false);

  const recognitionRef = useRef(null);
  const sessionIdRef = useRef(null);
  const shouldListenRef = useRef(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) setSupported(false);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, interim]);

  const speak = (text) => {
    if (muted || !text) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "it-IT";
      utter.rate = 1.0;
      utter.pitch = 1.0;
      const voices = window.speechSynthesis.getVoices();
      const it = voices.find((v) => v.lang?.startsWith("it")) || voices.find((v) => v.lang?.startsWith("it-IT"));
      if (it) utter.voice = it;
      utter.onend = () => {
        // Riprendi ascolto dopo che Emma ha finito di parlare
        if (shouldListenRef.current) startListening();
      };
      window.speechSynthesis.speak(utter);
    } catch (e) { console.error(e); }
  };

  const sendToBackend = async (userText) => {
    if (!userText.trim()) return;
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setThinking(true);
    try {
      const { data } = await api.post("/voicebot/converse", {
        session_id: sessionIdRef.current,
        user_message: userText,
      });
      setMessages((m) => {
        const next = [...m, { role: "assistant", text: data.reply, actions: data.actions }];
        if (data.ticket_number) {
          next.push({ role: "system", text: `Ticket ${data.ticket_number} aperto ✓`, ticketNumber: data.ticket_number });
        }
        return next;
      });
      speak(data.reply);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setThinking(false);
    }
  };

  const startListening = () => {
    if (!supported || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "it-IT";
    rec.continuous = false;
    rec.interimResults = true;
    let finalText = "";
    rec.onresult = (ev) => {
      let interimT = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t;
        else interimT += t;
      }
      setInterim(interimT);
    };
    rec.onerror = (ev) => {
      console.warn("SR error", ev.error);
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterim("");
      if (finalText.trim()) sendToBackend(finalText.trim());
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) { console.error(e); }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  const startCall = () => {
    sessionIdRef.current = crypto.randomUUID();
    setMessages([]);
    setCallActive(true);
    shouldListenRef.current = true;
    // Emma inizia parlando lei
    const opening = "Buongiorno, Wineemotion Assistenza, sono Emma. Come posso aiutarti?";
    setMessages([{ role: "assistant", text: opening }]);
    speak(opening);
    // startListening verrà chiamato dalla utter.onend
  };

  const endCall = () => {
    shouldListenRef.current = false;
    stopListening();
    window.speechSynthesis?.cancel();
    setCallActive(false);
    setMessages((m) => [...m, { role: "system", text: "Chiamata terminata." }]);
  };

  const sendTyped = (e) => {
    e.preventDefault();
    const val = e.target.elements.msg.value.trim();
    if (!val) return;
    e.target.reset();
    if (!sessionIdRef.current) sessionIdRef.current = crypto.randomUUID();
    if (!callActive) setCallActive(true);
    sendToBackend(val);
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="voicebot-demo-page">
      <PageHeader
        title="Voicebot AI — Demo"
        eyebrow="Risponditore intelligente"
        subtitle="Simulazione in-browser di una chiamata al risponditore AI di Wineemotion. Usa il microfono del PC o del telefono."
      />

      {!supported && (
        <Card className="rounded-sm border-orange-200 bg-orange-50 p-4 mb-4">
          <div className="text-sm text-orange-800">
            Il tuo browser non supporta il riconoscimento vocale. Prova con <b>Chrome</b>, <b>Edge</b> o
            <b> Safari</b>. Puoi comunque scrivere per testare la conversazione AI qui sotto.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Colonna centrale — Chiamata */}
        <Card className="rounded-sm p-6 lg:col-span-2 border-zinc-200 min-h-[500px] flex flex-col">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-100">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Chiamata in corso</div>
              <div className="font-display text-lg font-semibold text-zinc-950 mt-0.5">
                Emma — Assistente Wineemotion
              </div>
            </div>
            <div className="flex items-center gap-2">
              {callActive && (
                <Badge className={`rounded-sm font-mono text-[10px] ${listening ? "bg-red-600" : "bg-zinc-500"}`}>
                  {listening ? "● ASCOLTO" : thinking ? "…PENSA" : "MUTO"}
                </Badge>
              )}
            </div>
          </div>

          {/* Transcript */}
          <div className="flex-1 overflow-y-auto max-h-[420px] pr-1 space-y-2" data-testid="voicebot-transcript">
            {messages.length === 0 && !callActive && (
              <div className="h-64 flex items-center justify-center text-sm text-zinc-500 text-center">
                <div>
                  <PhoneCall className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
                  Premi "Inizia chiamata" per parlare con Emma.<br />
                  Prova a dire: <span className="italic">"Il mio dispenser non eroga più il vino rosso"</span>.
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              if (m.role === "user") return (
                <div key={i} className="flex justify-end" data-testid={`voicebot-msg-user-${i}`}>
                  <div className="bg-brand text-white rounded-sm px-3 py-2 max-w-[80%] text-sm leading-relaxed">{m.text}</div>
                </div>
              );
              if (m.role === "assistant") return (
                <div key={i} className="flex justify-start" data-testid={`voicebot-msg-assistant-${i}`}>
                  <div className="bg-zinc-100 text-zinc-900 rounded-sm px-3 py-2 max-w-[80%] text-sm leading-relaxed">
                    <div className="text-[10px] uppercase tracking-widest text-brand mb-1">Emma</div>
                    {m.text}
                  </div>
                </div>
              );
              return (
                <div key={i} className="flex justify-center">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-1.5 text-xs text-emerald-800 flex items-center gap-1.5">
                    {m.ticketNumber && <TicketCheck className="h-3.5 w-3.5" />}
                    {m.text}
                  </div>
                </div>
              );
            })}
            {interim && (
              <div className="flex justify-end">
                <div className="bg-brand/50 text-white rounded-sm px-3 py-2 max-w-[80%] text-sm italic">{interim}…</div>
              </div>
            )}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-zinc-100 rounded-sm px-3 py-2 text-sm text-zinc-500 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Emma sta pensando…
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Fallback textbox */}
          <form onSubmit={sendTyped} className="mt-3 pt-3 border-t border-zinc-100 flex gap-2">
            <input
              name="msg"
              placeholder='Oppure scrivi qui (es. "Il mio dispenser 220621000123 perde gas")'
              className="flex-1 h-9 px-3 rounded-sm border border-zinc-200 text-sm focus:outline-none focus:border-brand"
              data-testid="voicebot-input"
            />
            <Button type="submit" variant="outline" className="rounded-sm h-9 text-xs">Invia</Button>
          </form>
        </Card>

        {/* Colonna destra — Controlli */}
        <Card className="rounded-sm p-6 border-zinc-200 flex flex-col gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Controlli</div>
            {!callActive ? (
              <Button
                onClick={startCall}
                disabled={!supported}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-12 font-semibold"
                data-testid="voicebot-start"
              >
                <PhoneCall className="h-4 w-4 mr-2" /> Inizia chiamata
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={endCall}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-12"
                  data-testid="voicebot-end"
                >
                  <PhoneOff className="h-4 w-4 mr-2" /> Termina chiamata
                </Button>
                {listening ? (
                  <Button onClick={stopListening} variant="outline" className="w-full rounded-sm h-10">
                    <MicOff className="h-4 w-4 mr-2" /> Ferma microfono
                  </Button>
                ) : (
                  <Button onClick={startListening} variant="outline" className="w-full rounded-sm h-10" disabled={thinking}>
                    <Mic className="h-4 w-4 mr-2" /> Parla ora
                  </Button>
                )}
                <Button
                  onClick={() => setMuted((v) => !v)}
                  variant="outline"
                  className="w-full rounded-sm h-10"
                >
                  <Volume2 className={`h-4 w-4 mr-2 ${muted ? "opacity-40" : ""}`} />
                  {muted ? "Voce OFF" : "Voce ON"}
                </Button>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-zinc-100">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Come funziona</div>
            <ul className="text-xs text-zinc-600 space-y-1.5 leading-relaxed">
              <li>› Emma ascolta dal microfono e trascrive con Web Speech API.</li>
              <li>› Il testo va al backend che risponde via <b>Gemini 2.5 Flash</b>.</li>
              <li>› Se serve, cerca il dispenser dal seriale e apre un ticket reale nel DB.</li>
              <li>› La risposta viene letta in italiano tramite la sintesi vocale del browser.</li>
            </ul>
          </div>

          <div className="pt-3 border-t border-zinc-100">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Da provare</div>
            <ul className="text-xs text-zinc-600 space-y-1.5 leading-relaxed">
              <li>· "Il mio dispenser non eroga più il vino"</li>
              <li>· "Perdo gas dallo sportello numero 2"</li>
              <li>· Detta il seriale a voce, cifra per cifra</li>
              <li>· "Il display è spento e i vini sono sotto servizio"</li>
            </ul>
          </div>

          <div className="text-[10px] text-zinc-400 mt-auto">
            Demo browser-only. In produzione richiede numero Twilio o ElevenLabs per chiamate reali.
          </div>
        </Card>
      </div>
    </div>
  );
}
